import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ShieldCheck, Wrench, ArrowRight, Sparkles } from "lucide-react";
import AppShell from "../../components/argus/AppShell";
import { Button } from "../../components/ui/button";
import { useAuth } from "../../lib/auth";

const slides = [
  {
    key: "welcome",
    title: "Welcome to ArgusSecure.",
    body: "Three tools, one dashboard. Find it before they do.",
    tiles: [
      { Icon: Search, h: "Find.", p: "SAST, DAST, SCA — together." },
      { Icon: ShieldCheck, h: "Prove.", p: "Evidence on every finding." },
      { Icon: Wrench, h: "Fix.", p: "A fix beside every flaw." },
    ],
  },
  {
    key: "demo",
    title: "We've already loaded a demo for you.",
    body: "Three demo projects are pre-scanned and waiting. Tour the findings, then point ArgusSecure at something real.",
  },
  {
    key: "tour",
    title: "Here's the lay of the land.",
    body: "Sidebar = where you live. Findings = your triage queue. Reports = what your CEO asks for. Settings = tokens, members, billing.",
  },
  {
    key: "go",
    title: "Ready to scan something real?",
    body: "One click, three project types. Pick the one that matches what you ship.",
  },
];

export default function Welcome() {
  const [i, setI] = useState(0);
  const nav = useNavigate();
  const { user } = useAuth();
  const s = slides[i];
  const next = () => (i === slides.length - 1 ? nav("/app/projects") : setI(i + 1));
  const skip = () => nav("/app");

  return (
    <AppShell title="Welcome">
      <div className="max-w-2xl mx-auto" data-testid="welcome-card">
        <div className="rounded-2xl bg-paper border border-line shadow-sm p-8 md:p-12">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-gold mb-3">
            <Sparkles size={14} /> Step {i + 1} of {slides.length}
          </div>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight" data-testid="welcome-title">
            {i === 0 ? `Welcome, ${user?.name?.split(" ")[0] || "friend"}.` : s.title}
          </h2>
          <p className="text-ink/70 mt-3 text-lg">{i === 0 ? s.body : s.body}</p>

          {s.tiles && (
            <div className="grid sm:grid-cols-3 gap-4 mt-8">
              {s.tiles.map(({ Icon, h, p }) => (
                <div key={h} className="rounded-xl border border-line p-5">
                  <Icon size={22} className="text-gold mb-3" />
                  <div className="font-display text-xl">{h}</div>
                  <div className="text-sm text-ink/60 mt-1">{p}</div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-10 flex flex-wrap items-center justify-between gap-3">
            <button onClick={skip} className="text-sm text-ink/50 hover:text-ink" data-testid="welcome-skip">
              Skip
            </button>
            <Button onClick={next} className="h-12 px-6 bg-gold text-paper hover:bg-gold2 font-semibold" data-testid="welcome-next">
              {i === slides.length - 1 ? "Add My First Project" : "Continue"} <ArrowRight size={16} className="ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
